import PropTypes from 'prop-types';
// @mui
import { alpha, useTheme } from '@mui/material/styles';
import { Box, Card, Link, Stack, Typography } from '@mui/material';
// components
import Iconify from 'src/components/Iconify';
//
import { TEAM_FONT } from 'src/sections/@dashboard/user/teamTokens';

// ----------------------------------------------------------------------
// The small parts the record is built from.
//
// Two rules learned the hard way here. First: a label is not a headline.
// Setting every field name in spaced uppercase turned the page into a wall
// of shouting and flattened the real hierarchy, so labels are quiet and the
// VALUE is the thing with weight. Second: no boxes inside boxes. A grey card
// inside a white card on a grey page is three soft containers saying nothing;
// one card, ruled with hairlines, says more.

export const cardSx = (theme) => ({
  borderRadius: 2,
  border: `1px solid ${theme.palette.divider}`,
  boxShadow: 'none'
});

// ----------------------------------------------------------------------

Eyebrow.propTypes = { children: PropTypes.node, sx: PropTypes.object };

// The one place uppercase is allowed: naming a group of facts.
export function Eyebrow({ children, sx }) {
  return (
    <Typography
      variant="caption"
      sx={{
        display: 'block',
        fontFamily: TEAM_FONT,
        fontWeight: 700,
        fontSize: 11,
        letterSpacing: '0.1em',
        textTransform: 'uppercase',
        color: 'text.disabled',
        ...sx
      }}
    >
      {children}
    </Typography>
  );
}

// ----------------------------------------------------------------------

Fact.propTypes = {
  label: PropTypes.node,
  children: PropTypes.node,
  href: PropTypes.string,
  empty: PropTypes.string
};

// One fact: the name of it in quiet ink, the fact itself in the page's own
// voice. A fact with nothing in it says so — "not on file" is information.
export function Fact({ label, children, href, empty = 'Not on file' }) {
  const theme = useTheme();
  const blank =
    children === null ||
    children === undefined ||
    children === '' ||
    children === '—';
  return (
    <Stack
      direction="row"
      spacing={2}
      alignItems="baseline"
      sx={{
        py: 1.125,
        borderBottom: `1px solid ${alpha(theme.palette.divider, 0.7)}`,
        '&:last-of-type': { borderBottom: 0 }
      }}
    >
      <Typography
        variant="body2"
        sx={{
          width: 116,
          flex: 'none',
          color: 'text.secondary',
          lineHeight: 1.5
        }}
      >
        {label}
      </Typography>
      <Box sx={{ minWidth: 0, flex: 1, textAlign: 'right' }}>
        {blank ? (
          <Typography variant="body2" sx={{ color: 'text.disabled' }}>
            {empty}
          </Typography>
        ) : href ? (
          <Link
            href={href}
            target={href.startsWith('http') ? '_blank' : undefined}
            rel="noopener"
            underline="hover"
            variant="body2"
            sx={{ fontWeight: 500, wordBreak: 'break-word' }}
          >
            {children}
          </Link>
        ) : (
          <Typography
            variant="body2"
            sx={{ fontWeight: 500, wordBreak: 'break-word' }}
          >
            {children}
          </Typography>
        )}
      </Box>
    </Stack>
  );
}

// ----------------------------------------------------------------------

FactGroup.propTypes = {
  title: PropTypes.string,
  children: PropTypes.node,
  sx: PropTypes.object
};

export function FactGroup({ title, children, sx }) {
  return (
    <Box sx={{ ...sx }}>
      {title && <Eyebrow sx={{ mb: 0.5 }}>{title}</Eyebrow>}
      {children}
    </Box>
  );
}

// ----------------------------------------------------------------------

Panel.propTypes = {
  title: PropTypes.node,
  note: PropTypes.node,
  icon: PropTypes.string,
  action: PropTypes.node,
  children: PropTypes.node,
  flush: PropTypes.bool,
  id: PropTypes.string,
  sx: PropTypes.object
};

// A block of the record. One hairline card, a plain heading, and the content.
// No coloured tile, no explanatory sentence under every title: nine of those
// down a page is nine lines of grey noise.
export function Panel({
  title,
  note,
  icon,
  action,
  children,
  flush = false,
  id,
  sx
}) {
  const theme = useTheme();
  return (
    <Card id={id} sx={{ ...cardSx(theme), scrollMarginTop: 96, ...sx }}>
      {(title || action) && (
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          spacing={2}
          sx={{
            px: { xs: 2, sm: 2.5 },
            py: 1.75,
            borderBottom: `1px solid ${theme.palette.divider}`
          }}
        >
          <Stack
            direction="row"
            spacing={1.25}
            alignItems="center"
            sx={{ minWidth: 0 }}
          >
            {icon && (
              <Iconify
                icon={icon}
                width={18}
                height={18}
                sx={{ color: 'text.disabled', flex: 'none' }}
              />
            )}
            <Typography
              sx={{
                fontFamily: TEAM_FONT,
                fontWeight: 700,
                fontSize: 15.5,
                lineHeight: 1.3,
                minWidth: 0
              }}
              noWrap
            >
              {title}
            </Typography>
            {note && (
              <Typography
                variant="body2"
                noWrap
                sx={{ color: 'text.disabled', flex: 'none' }}
              >
                {note}
              </Typography>
            )}
          </Stack>
          {action && <Box sx={{ flex: 'none' }}>{action}</Box>}
        </Stack>
      )}
      <Box sx={{ p: flush ? 0 : { xs: 2, sm: 2.5 } }}>{children}</Box>
    </Card>
  );
}
